import { useState, useEffect, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { AuthProvider } from '@/contexts/AuthContext'
// import { AccessRoute } from './auth/AccessRoute'
import { TenantRoute } from '@/auth/TenantRoute'
import { AuthenticatedRoute } from '@/auth/AuthenticatedRoute'
import Navbar from '@/components/Navbar'
import PublicNavbar from '@/components/PublicNavbar'
import Sidebar from '@/components/Sidebar'
import Login from '@/pages/Login'
import ResetPasswordPage from '@/pages/Reset-Password'
import NotFound from '@/pages/Not-Found'
import Unauthorized from '@/pages/Unauthorized'
import { DashboardSkeleton } from '@shared/components/LoadingStates'
import { OnboardingProvider, useOnboarding } from '@/contexts/OnboardingContext'
import SetupWizard from '@/pages/SetupWizard'

function AuthedArea() {
  const { status, loading } = useOnboarding()
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => localStorage.getItem('ui.sidebarCollapsed') === 'true')
  
  if (loading) return <DashboardSkeleton />
  
  // If onboarding is not completed, show setup wizard without sidebar/navbar
  if (status && !status.onboarding_completed) {
    return <SetupWizard />
  }
  
  // If onboarding is completed, show main app with sidebar/navbar
  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev
      localStorage.setItem('ui.sidebarCollapsed', String(next))
      return next
    })
  }
  
  return (
    <>
      {/* Sidebar + App Navbar for authenticated area */}
      <Sidebar isCollapsed={isSidebarCollapsed} onToggle={toggleSidebar} />
      <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'md:ml-16' : 'md:ml-64'}`}>
        <Navbar/>
        <main className="pt-1 p-6">
          <Routes>
            <Route path="/" element={<TenantRoute><Suspense fallback={<DashboardSkeleton />}><Dashboard /></Suspense></TenantRoute>} />
            <Route path="/customers" element={<TenantRoute requiredFeatures={['customers']} requiredPermissions={['customers:read']}><Suspense fallback={<DashboardSkeleton />}><CustomersPage /></Suspense></TenantRoute>} />
            <Route path="/vehicles" element={<TenantRoute requiredFeatures={['vehicles']} requiredPermissions={['vehicles:read']}><Suspense fallback={<DashboardSkeleton />}><VehiclesPage /></Suspense></TenantRoute>} />
            <Route path="/service-requests" element={<TenantRoute requiredFeatures={['service_requests']} requiredPermissions={['service_requests:read']}><Suspense fallback={<DashboardSkeleton />}><ServiceRequestsPage /></Suspense></TenantRoute>} />
            <Route path="/service-requests/:id" element={<TenantRoute requiredPermissions={['service_requests:read']}><Suspense fallback={<DashboardSkeleton />}><ServiceRequestsPage /></Suspense></TenantRoute>} />
            <Route path="/inventory" element={<TenantRoute requiredFeatures={['inventory']} requiredPermissions={['inventory:read']}><Suspense fallback={<DashboardSkeleton />}><InventoryPage /></Suspense></TenantRoute>} />
            <Route path="/inventory-settings" element={<TenantRoute requiredFeatures={['inventory']} requiredPermissions={['inventory:read']}><Suspense fallback={<DashboardSkeleton />}><InventorySettingsPage /></Suspense></TenantRoute>} />
            <Route path="/invoices" element={<TenantRoute requiredFeatures={['invoices']} requiredPermissions={['invoices:read']}><Suspense fallback={<DashboardSkeleton />}><InvoicesPage /></Suspense></TenantRoute>} />
            <Route path="/invoice/:id" element={<TenantRoute requiredPermissions={['invoices:read']}><Suspense fallback={<DashboardSkeleton />}><InvoicePage /></Suspense></TenantRoute>} />
            <Route path="/calendar" element={<TenantRoute requiredFeatures={['calendar']} requiredPermissions={['calendar:read']}><Suspense fallback={<DashboardSkeleton />}><CalendarPage /></Suspense></TenantRoute>} />
            <Route path="/service-templates" element={<TenantRoute requiredFeatures={['templates']} requiredPermissions={['templates:read']}><Suspense fallback={<DashboardSkeleton />}><ServiceTemplatesPage /></Suspense></TenantRoute>} />
            <Route path="/documentation" element={<TenantRoute requiredFeatures={['docs']} requiredPermissions={['docs:read']}><Suspense fallback={<DashboardSkeleton />}><DocumentationPage /></Suspense></TenantRoute>} />
            <Route path="/documentation/create" element={<TenantRoute requiredFeatures={['docs']} requiredPermissions={['docs:create']}><Suspense fallback={<DashboardSkeleton />}><CreateDocumentPage /></Suspense></TenantRoute>} />
            <Route path="/documentation/edit/:id" element={<TenantRoute requiredFeatures={['docs']} requiredPermissions={['docs:update']}><Suspense fallback={<DashboardSkeleton />}><EditDocumentPage /></Suspense></TenantRoute>} />
            <Route path="/documentation/view/:id" element={<TenantRoute requiredFeatures={['docs']} requiredPermissions={['docs:read']}><Suspense fallback={<DashboardSkeleton />}><ViewDocumentPage /></Suspense></TenantRoute>} />
            <Route path="/documentation-settings" element={<TenantRoute requiredFeatures={['docs']} requiredPermissions={['docs:update']}><Suspense fallback={<DashboardSkeleton />}><DocumentationSettingsPage /></Suspense></TenantRoute>} />
            <Route path="/audit-log" element={<TenantRoute requiredFeatures={['audit_logs']}><Suspense fallback={<DashboardSkeleton />}><AuditLogPage /></Suspense></TenantRoute>} />
            <Route path="/shop-settings" element={<TenantRoute requiredPermissions={['settings:read']}><Suspense fallback={<DashboardSkeleton />}><ShopSettingsPage /></Suspense></TenantRoute>} />
            <Route path="/roles" element={<TenantRoute requiredFeatures={['roles']} requiredPermissions={['roles:read']}><Suspense fallback={<DashboardSkeleton />}><RolesPage /></Suspense></TenantRoute>} />
            <Route path="/vehicle-service-history/:vehicleId" element={<TenantRoute requiredFeatures={['vehicle_service_history']} requiredPermissions={['service_requests:read']}><Suspense fallback={<DashboardSkeleton />}><VehicleServiceHistoryPage /></Suspense></TenantRoute>} />
            <Route path="/account-settings" element={<TenantRoute><Suspense fallback={<DashboardSkeleton />}><AccountSettingsPage /></Suspense></TenantRoute>} />
            <Route path="/users" element={<TenantRoute requiredPermissions={['users:read']}><Suspense fallback={<DashboardSkeleton />}><UsersPage /></Suspense></TenantRoute>} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </div>
    </>
  )
}

// Lazy load route components for code splitting
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const CustomersPage = lazy(() => import('@/pages/Customers'))
const VehiclesPage = lazy(() => import('@/pages/Vehicles'))
const ServiceRequestsPage = lazy(() => import('@/pages/ServiceRequests'))
const InventoryPage = lazy(() => import('@/pages/Inventory'))
const InventorySettingsPage = lazy(() => import('@/pages/Inventory-Settings'))
const UsersPage = lazy(() => import('@/pages/Users'))
// Lazy load remaining route components
const InvoicesPage = lazy(() => import('@/pages/Invoices'))
const InvoicePage = lazy(() => import('@/pages/Invoice'))
const CalendarPage = lazy(() => import('@/pages/Calendar'))
const ServiceTemplatesPage = lazy(() => import('@/pages/Service-Templates'))
const ShopSettingsPage = lazy(() => import('@/pages/Shop-Settings'))
const RolesPage = lazy(() => import('@/pages/RolesPage'))
const AuditLogPage = lazy(() => import('@/pages/Audit-Log'))
const AccountSettingsPage = lazy(() => import('@/pages/Account-Settings'))
const VehicleServiceHistoryPage = lazy(() => import('@/pages/Vehicle-Service-History'))
const DocumentationPage = lazy(() => import('@/pages/Documentation'))
const CreateDocumentPage = lazy(() => import('@/pages/CreateDocument'))
const EditDocumentPage = lazy(() => import('@/pages/EditDocument'))
const ViewDocumentPage = lazy(() => import('@/pages/ViewDocument'))
const DocumentationSettingsPage = lazy(() => import('@/pages/Documentation-Settings'))

function TitleManager() {
  const location = useLocation()

  useEffect(() => {
    const pathname = location.pathname || '/'

    const toTitleCase = (value: string) =>
      value
        .replace(/[/_-]+/g, ' ')
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase())

    const getPageName = (path: string) => {
      if (path === '/' ) return 'Dashboard';
      if (path === '/login') return 'Login';
      const segments = path.split('/').filter(Boolean)
      return segments.length ? toTitleCase(segments[segments.length - 1]) : 'Dashboard'
    }

    const shopName = 'Motiv'
    const pageName = getPageName(pathname)
    document.title = `${shopName} - ${pageName}`
  }, [location.pathname])

  return null
}

function App() {
  // Initialize theme on app load
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  return (
    <Router>
      <AuthProvider>
      <OnboardingProvider>
      <TitleManager />
      <div className="min-h-screen bg-background">
        {/* Conditional layout based on route: login page uses public navbar */}
        <Routes>
          <Route path="/login" element={<>
            <PublicNavbar />
            <main className="pt-16 p-6">
              <AuthenticatedRoute>
                <Login />
              </AuthenticatedRoute>
            </main>
          </>} />
          <Route path="/reset-password" element={<>
            <PublicNavbar />
            <main className="pt-16 p-6">
              <AuthenticatedRoute>
                <ResetPasswordPage />
              </AuthenticatedRoute>
            </main>
          </>} />
          <Route path="*" element={<AuthedArea />} />
        </Routes>
      </div>
      </OnboardingProvider>
      </AuthProvider>
    </Router>
  )
}

export default App 